var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Reservation, ReservationResponse, } from '@data/models';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import TimeUtils from '@business/utils/time';
import { isAllotmentBlockCode } from '@business/utils/promocodes';
import ReservationStatusResponse from '@data/models/ReservationStatusResponse';
import { apiCall } from '../axios';
// Time threshold in milliseconds to consider a request as the same request and return the same promise instead of making a new request
export const SAME_REQUEST_THRESHOLD = 5000;
const AddressKeys = [
    'address1',
    'address2',
    'country',
    'city',
    'state',
    'zipCode',
];
const isCustomFieldKey = (key) => key.startsWith('cf_');
const typedKeys = (record) => Object.keys(record);
const additionalGuestKey = (field, index) => {
    switch (field) {
        case 'first_name':
            return `additional_first[${index}]`;
        case 'last_name':
            return `additional_last[${index}]`;
        default:
            return `add_${field}[${index}]`;
    }
};
const additionalCustomFieldKey = (field, index) => `add_${field}[${index}]`;
// Custom field answers live on the guest as `cf_<id>` next to the model fields
const getGuestCustomFields = (guest) => Object.entries(guest).reduce((acc, [key, value]) => {
    if (isCustomFieldKey(key) && typeof value === 'string') {
        acc[key] = value;
    }
    return acc;
}, {});
const getGuestPhoneNumber = (phone, countryDialCode) => {
    if (!countryDialCode || phone.startsWith(countryDialCode)) {
        return phone;
    }
    const parts = countryDialCode.split('-');
    if (parts.length !== 2) {
        return phone;
    }
    const dialCode = parts[1];
    if (phone.startsWith(dialCode)) {
        return phone;
    }
    return `${parts[1]}${phone}`;
};
/**
  This function is used to add the guest information to the reservation request
  The main guest is passed sending the information directly, but the additional
  guests are passed using the add_ prefix and the index of the guest
  that is readed as an array in the backend
*/
function addGuestInformation(data, guests, index = 0) {
    const guest = Object.assign({}, guests[index]);
    const isMainGuest = index === 0;
    // Same address as main guest
    if (!isMainGuest && guest.isSameAddressAsMain === '1') {
        AddressKeys.forEach((key) => {
            guest[key] = guests[0][key];
        });
    }
    const guestFields = {
        address1: guest.address1,
        address2: guest.address2,
        city: guest.city,
        company_name: guest.companyName,
        company_tax_id_number: guest.companyTaxIdNumber,
        country: guest.country,
        email: guest.email,
        first_name: guest.firstName,
        gender: guest.gender,
        guest_tax_id_number: guest.taxIdNumber,
        last_name: guest.lastName,
        phone: getGuestPhoneNumber(guest.phone || '', guest.countryDialCode),
        state: guest.state,
        zip: guest.zipCode,
    };
    const customFields = getGuestCustomFields(guest);
    if (isMainGuest) {
        Object.assign(data, guestFields, customFields);
        data.booking_estimated_arrival_time = guest.estimatedArrivalTime;
        if (guest.isOptIn !== undefined) {
            data.is_opt_in = guest.isOptIn ? '1' : '0';
        }
        return;
    }
    typedKeys(guestFields).forEach((field) => {
        data[additionalGuestKey(field, index)] = guestFields[field];
    });
    typedKeys(customFields).forEach((field) => {
        data[additionalCustomFieldKey(field, index)] = customFields[field];
    });
}
function stringifyPaymentSDKBillingDetails(billingDetails) {
    const data = {
        address1: billingDetails.billingAddress1,
        address2: billingDetails.billingAddress2,
        city: billingDetails.billingCity,
        country: billingDetails.billingCountry,
        document_number: billingDetails.billingDocumentNumber,
        document_type: billingDetails.billingDocumentType,
        email: billingDetails.billingEmail,
        state: billingDetails.billingState,
        zip: billingDetails.billingZip,
    };
    return JSON.stringify(data);
}
/**
 * BENG-4224: when the payment page looks stuck, guests click "Continue" again
 * and every click creates a reservation. Repeated calls for the same cart
 * within SAME_REQUEST_THRESHOLD get the first promise instead of a second POST.
 *
 * This guards against accidental multi-clicks, not against duplicates in
 * general. Past the threshold the next click deliberately goes through — even
 * if the first call is still in flight — so a guest is never locked out of a
 * checkout that looks stuck. Making that retry safe is the backend's job, via
 * the `idempotency_key` we send below.
 */
let pendingPrepare = null;
export class ReservationService {
    static confirmReservation(_a) {
        return __awaiter(this, arguments, void 0, function* ({ propertyId, reservationCode, }) {
            return apiCall(ReservationResponse)('post', '/confirm', {
                data_res: reservationCode,
                property_id: propertyId,
            });
        });
    }
    static getReservation(reservationCode) {
        const url = '/confirmation?data_res=' + reservationCode;
        return apiCall(Reservation)('get', url);
    }
    static getReservationStatus(reservationCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = '/status?data_res=' + reservationCode;
            return apiCall(ReservationStatusResponse)('get', url);
        });
    }
    static makeReservation(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { aiSource, referralId, propertyId, rooms, externalOffers, checkin, checkout, guests, payment, currency, language, code, origin, googleHotelAds, smartPolicyId, smartPolicyRootId, cartToken, externalOffersShown, sessionId, bookingEngineSource, iframe, widgetSource, termsAccepted, } = request;
            const attemptKey = cartToken;
            const requestTime = Date.now();
            if ((pendingPrepare === null || pendingPrepare === void 0 ? void 0 : pendingPrepare.key) === attemptKey &&
                requestTime - pendingPrepare.requestTime < SAME_REQUEST_THRESHOLD) {
                return pendingPrepare.promise;
            }
            const data = {
                agree: termsAccepted ? 1 : 0,
                // Data used for metrics and analysis of the incoming bookings
                ai_source: aiSource,
                association_id: referralId,
                booking_engine_source: bookingEngineSource,
                // Include session tokens and secrets
                cart_token: cartToken,
                // External offers flags
                cfarOffersPresented: externalOffersShown,
                currency: currency.toUpperCase(),
                iframe,
                lang: language,
                origin,
                // The Payment SDK is the sole payment path (BENG-3774); this is always true.
                payment_sdk: true,
                rooms: JSON.stringify(rooms),
                selectedLinks: undefined,
                selected_checkin: TimeUtils.format(checkin),
                selected_checkout: TimeUtils.format(checkout),
                sessionId,
                // Smart Policy Id and Root Id, if available
                smart_policy_id: smartPolicyId,
                smart_policy_root_id: smartPolicyRootId,
                widget_property: propertyId,
                widget_source: widgetSource,
            };
            // BENG-4224: the cart token doubles as the idempotency key. It is minted by
            // /calculateTotals — so it is stable across repeated submits of the same
            // cart, and changes whenever the cart does. The backend keeps a 12h Redis
            // lock per key and replays the stored reservation instead of creating a
            // second one. Not covered: a page reload mints a new cart token.
            if (cartToken &&
                FeatureFlagManager.isEnabled(FeatureFlag.sendPrepareIdempotencyKey)) {
                data.idempotency_key = cartToken;
            }
            // guests ----------------------------------
            for (let i = 0; i < guests.length; i++) {
                addGuestInformation(data, guests, i);
            }
            // sending promo codes as the allotment block code will throw an error
            if (code && isAllotmentBlockCode(code)) {
                data.allotment_block_code = code;
            }
            if (externalOffers === null || externalOffers === void 0 ? void 0 : externalOffers.length) {
                data.externalOffers = JSON.stringify(externalOffers);
            }
            // payment info ----------------------------
            data.payment_gateway = payment.gateway;
            // The Payment sdk is now using 'card' instead of 'cards',
            // so it is needed to keep the compatibility with the backend
            data.payment_method = payment.method === 'card' ? 'cards' : payment.method;
            data.paymentMethodHash = payment.hash;
            data.token_card = payment.token;
            if (payment.method === 'card' && payment.hash && payment.billingDetails) {
                data.billing_address = stringifyPaymentSDKBillingDetails(payment.billingDetails);
            }
            // Add all possible additional gha data
            if (origin === 'gha' && googleHotelAds) {
                Object.keys(googleHotelAds).forEach((key) => {
                    if (googleHotelAds[key] !== undefined) {
                        data[key] = googleHotelAds[key];
                    }
                });
            }
            // Include click tracking data if available (for Amplify attribution)
            if (payment.clickId) {
                data.cb_amp_click_id = payment.clickId;
            }
            const promise = apiCall(ReservationResponse)('post', '/prepare', data);
            pendingPrepare = { key: attemptKey, promise, requestTime };
            return promise;
        });
    }
}
